/**
 * Tag management page for /workspace/tags route.
 * Allows workspace owners/admins to create, rename, recolor, and delete tags.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../components/layout/PageHeader";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { useTags } from "../hooks/useTags";
import {
  createTag,
  updateTag,
  deleteTag,
  type Tag,
} from "../services/naraDoor";
import {
  Tag as TagIcon,
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  Loader2,
  ArrowLeft,
  AlertTriangle,
} from "lucide-react";
import { publicRoutes } from "../lib/routes";

const PRESET_COLORS = [
  "#EF4444",
  "#F97316",
  "#EAB308",
  "#22C55E",
  "#06B6D4",
  "#3B82F6",
  "#8B5CF6",
  "#EC4899",
];

function ColorPicker({
  selected,
  onChange,
}: {
  selected: string | undefined;
  onChange: (color: string | undefined) => void;
}) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* No color option */}
      <button
        type="button"
        onClick={() => onChange(undefined)}
        className={`w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all ${
          selected === undefined
            ? "border-blue-500 ring-2 ring-blue-200"
            : "border-border hover:border-muted-foreground"
        }`}
        title="No color"
      >
        <X className="h-3 w-3 text-muted-foreground" />
      </button>
      {PRESET_COLORS.map((color) => (
        <button
          key={color}
          type="button"
          onClick={() => onChange(color)}
          className={`w-7 h-7 rounded-full border-2 transition-all ${
            selected === color
              ? "border-blue-500 ring-2 ring-blue-200 scale-110"
              : "border-transparent hover:scale-110"
          }`}
          style={{ backgroundColor: color }}
          title={color}
        />
      ))}
    </div>
  );
}

function UsageBadge({ count, label }: { count: number; label: string }) {
  if (count === 0) return null;
  return (
    <span className="text-xs text-muted-foreground">
      {count} {label}
    </span>
  );
}

export function WorkspaceTags() {
  const navigate = useNavigate();
  const { activeWorkspace, isLoading: isWorkspaceLoading } = useWorkspace();

  const { tags, isLoading: loading, isError: loadFailed } = useTags();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Form state for create/edit
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formColor, setFormColor] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setFormName("");
    setFormDescription("");
    setFormColor(undefined);
    setCreating(false);
    setEditingId(null);
    setError(null);
  };

  const startCreate = () => {
    resetForm();
    setCreating(true);
    setEditingId(null);
  };

  const startEdit = (tag: Tag) => {
    setFormName(tag.name);
    setFormDescription(tag.description || "");
    setFormColor(tag.color || undefined);
    setEditingId(tag.id);
    setCreating(false);
    setError(null);
  };

  const handleCreate = async () => {
    if (!activeWorkspace?.id || !formName.trim()) return;
    try {
      setSaving(true);
      setError(null);
      await createTag({
        workspaceId: activeWorkspace.id,
        name: formName.trim(),
        description: formDescription.trim() || undefined,
        color: formColor,
      });
      resetForm();
    } catch (err) {
      console.error("Failed to create tag:", err);
      setError("Failed to create tag. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingId || !formName.trim()) return;
    try {
      setSaving(true);
      setError(null);
      await updateTag(
        editingId,
        {
          name: formName.trim(),
          description: formDescription.trim() || undefined,
          color: formColor,
        },
        activeWorkspace?.id
      );
      resetForm();
    } catch (err) {
      console.error("Failed to update tag:", err);
      setError("Failed to update tag. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tag: Tag) => {
    if (!activeWorkspace?.id) return;
    try {
      setSaving(true);
      setError(null);
      await deleteTag(tag.id, activeWorkspace.id);
      setDeletingId(null);
    } catch (err) {
      console.error("Failed to delete tag:", err);
      setError("Failed to delete tag. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Get dashboard URL based on workspace handle
  const dashboardUrl = activeWorkspace?.handle
    ? `/ws/${activeWorkspace.handle}/dashboard`
    : publicRoutes.home;

  // Loading state
  if (isWorkspaceLoading || loading) {
    return (
      <div>
        <PageHeader
          title="Tags"
          breadcrumbs={[
            { label: "Dashboard", href: dashboardUrl },
            { label: "Settings", href: publicRoutes.workspaceSettings },
            { label: "Tags" },
          ]}
        />
        <div className="space-y-4">
          <div className="h-12 bg-muted rounded-lg animate-pulse" />
          <div className="h-64 bg-muted rounded-lg animate-pulse" />
        </div>
      </div>
    );
  }

  // No workspace selected
  if (!activeWorkspace) {
    return (
      <div>
        <PageHeader
          title="Tags"
          breadcrumbs={[
            { label: "Dashboard", href: publicRoutes.home },
            { label: "Settings", href: publicRoutes.workspaceSettings },
            { label: "Tags" },
          ]}
        />
        <div className="flex items-center gap-3 p-4 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <AlertTriangle className="h-5 w-5 text-yellow-600 shrink-0" />
          <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
            No workspace selected. Please select a workspace first.
          </p>
        </div>
      </div>
    );
  }

  const tagAccess = (activeWorkspace?.settings?.tags as any)?.access || "custom";

  // Tags disabled for this workspace
  if (tagAccess === "none") {
    return (
      <div>
        <PageHeader
          title="Tags"
          breadcrumbs={[
            { label: "Dashboard", href: dashboardUrl },
            { label: "Settings", href: publicRoutes.workspaceSettings },
            { label: "Tags" },
          ]}
          actions={
            <button
              onClick={() => navigate(publicRoutes.workspaceSettings)}
              className="inline-flex items-center gap-2 px-3 py-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Settings
            </button>
          }
        />
        <div className="flex items-center gap-3 p-4 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <AlertTriangle className="h-5 w-5 text-yellow-600 shrink-0" />
          <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
            Tags are disabled for this workspace. An administrator can enable them in workspace settings.
          </p>
        </div>
      </div>
    );
  }

  const renderForm = (onSave: () => void, saveLabel: string) => (
    <div className="bg-card rounded-lg border border-border p-5 space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label
            htmlFor="tagName"
            className="block text-sm font-medium text-foreground mb-1"
          >
            Name <span className="text-red-500">*</span>
          </label>
          <input
            id="tagName"
            type="text"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder="e.g. Kitchen Reno, Phase 1"
            className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-background text-foreground"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && formName.trim()) onSave();
              if (e.key === "Escape") resetForm();
            }}
          />
        </div>
        <div>
          <label
            htmlFor="tagDescription"
            className="block text-sm font-medium text-foreground mb-1"
          >
            Description
          </label>
          <input
            id="tagDescription"
            type="text"
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            placeholder="Optional description"
            className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-background text-foreground"
            onKeyDown={(e) => {
              if (e.key === "Enter" && formName.trim()) onSave();
              if (e.key === "Escape") resetForm();
            }}
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Color
        </label>
        <ColorPicker selected={formColor} onChange={setFormColor} />
      </div>
      <div className="flex items-center gap-2 pt-2">
        <button
          onClick={onSave}
          disabled={!formName.trim() || saving}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Check className="h-4 w-4" />
          )}
          {saveLabel}
        </button>
        <button
          onClick={resetForm}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors text-sm"
        >
          <X className="h-4 w-4" />
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader
        title="Tags"
        description={`Manage tags for ${activeWorkspace.name}`}
        breadcrumbs={[
          { label: "Dashboard", href: dashboardUrl },
          { label: "Settings", href: publicRoutes.workspaceSettings },
          { label: "Tags" },
        ]}
        actions={
          <button
            onClick={() => navigate(publicRoutes.workspaceSettings)}
            className="inline-flex items-center gap-2 px-3 py-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Settings
          </button>
        }
      />

      {/* Error banner */}
      {(error || loadFailed) && (
        <div className="flex items-center gap-3 p-4 mb-6 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-300">
            {error ?? "Failed to load tags. Please try again."}
          </p>
          {error && (
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {/* New Tag button / create form */}
      <div className="mb-6">
        {creating ? (
          renderForm(handleCreate, "Create Tag")
        ) : tagAccess === "custom" ? (
          <button
            onClick={startCreate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            <Plus className="h-4 w-4" />
            New Tag
          </button>
        ) : null}
      </div>

      {/* Tag list */}
      {tags.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-lg border border-border">
          <TagIcon className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">
            No tags yet. Create your first tag to start organizing projects.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {tags.map((tag) => (
            <div key={tag.id}>
              {editingId === tag.id ? (
                renderForm(handleUpdate, "Save Changes")
              ) : (
                <div className="bg-card rounded-lg border border-border p-4 flex items-center gap-4 group hover:border-blue-200 dark:hover:border-blue-800 transition-colors">
                  {/* Color dot */}
                  <div
                    className="w-4 h-4 rounded-full shrink-0 border border-black/10"
                    style={{
                      backgroundColor: tag.color || "#9CA3AF",
                    }}
                  />

                  {/* Tag info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground truncate">
                        {tag.name}
                      </span>
                    </div>
                    {tag.description && (
                      <p className="text-sm text-muted-foreground truncate mt-0.5">
                        {tag.description}
                      </p>
                    )}
                    <div className="flex items-center gap-3 mt-1">
                      <UsageBadge
                        count={tag.project_count}
                        label={
                          tag.project_count === 1 ? "project" : "projects"
                        }
                      />
                      <UsageBadge
                        count={tag.document_count}
                        label={
                          tag.document_count === 1 ? "document" : "documents"
                        }
                      />
                      <UsageBadge
                        count={tag.file_count}
                        label={tag.file_count === 1 ? "file" : "files"}
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  {deletingId === tag.id ? (
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-sm text-red-600">
                        Delete?
                        {(tag.project_count > 0 ||
                          tag.document_count > 0 ||
                          tag.file_count > 0) && (
                          <span className="text-xs ml-1">
                            (
                            {[
                              tag.project_count > 0 &&
                                `${tag.project_count} project${tag.project_count !== 1 ? "s" : ""}`,
                              tag.document_count > 0 &&
                                `${tag.document_count} document${tag.document_count !== 1 ? "s" : ""}`,
                              tag.file_count > 0 &&
                                `${tag.file_count} file${tag.file_count !== 1 ? "s" : ""}`,
                            ]
                              .filter(Boolean)
                              .join(", ")}{" "}
                            will be untagged)
                          </span>
                        )}
                      </span>
                      <button
                        onClick={() => handleDelete(tag)}
                        disabled={saving}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors text-sm"
                      >
                        {saving ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                        Confirm
                      </button>
                      <button
                        onClick={() => setDeletingId(null)}
                        disabled={saving}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-foreground border border-border rounded-lg hover:bg-accent transition-colors text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => startEdit(tag)}
                        className="p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
                        title="Edit tag"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeletingId(tag.id)}
                        className="p-2 text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg transition-colors"
                        title="Delete tag"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
